import React from "react";
import PropTypes from "prop-types";
import { Dropdown, Button } from "semantic-ui-react";
import { Dropdown as FormikDropdown } from "formik-semantic-ui";

import { InvoiceStatuses } from "utilities/dist/invoice/constants";
import { DeliveryTypes } from "utilities/dist/order/constants";

export const statusMapping = {
    [InvoiceStatuses.CANCELLED]: {
        color: "red",
        icon: "close",
        text: "Cancelled",
    },
    [InvoiceStatuses.DRAFT]: {
        color: "yellow",
        icon: "clock",
        text: "Draft",
    },
    [InvoiceStatuses.READY_FOR_PICK]: {
        color: "orange",
        icon: "box",
        text: "Ready for Pick",
    },
    [InvoiceStatuses.PICK_COMPLETE]: {
        color: "olive",
        icon: "checkmark box",
        text: "Pick Complete",
    },
    [InvoiceStatuses.READY_FOR_TABC_STAMPS]: {
        color: "brown",
        icon: "certificate",
        text: "Ready for TABC Stamps",
    },
    [InvoiceStatuses.PARTIALLY_PAID]: {
        color: "teal",
        icon: "money",
        text: "Partially Paid",
    },
    [InvoiceStatuses.PAID]: {
        color: "green",
        icon: "dollar",
        text: "Paid",
    },
    [InvoiceStatuses.INVOICED]: {
        color: "blue",
        icon: "newspaper",
        text: "Invoiced",
    },
    [InvoiceStatuses.DELIVERED]: {
        color: "green",
        icon: "truck",
        text: "Delivered",
    },
    [InvoiceStatuses.PICKED_UP]: {
        color: "green",
        icon: "dolly",
        text: "Picked Up",
    },
    [InvoiceStatuses.PAYMENT_DUE]: {
        color: "purple",
        icon: "hourglass end",
        text: "Due",
        hidden: true,
    },
    [InvoiceStatuses.PAYMENT_DUE_SOON]: {
        color: "orange",
        icon: "hourglass half",
        text: "Due Soon",
        hidden: true,
    },
};

// List of statuses for dropdown (payment_due and payment_due_soon are hidden from UI)
export const VISIBLE_INVOICE_STATUSES = Object.values(InvoiceStatuses).filter(
    value => !statusMapping[value]?.hidden
);

export const ALLOWED_NEXT_STATUSES = {
    [InvoiceStatuses.DRAFT]: [InvoiceStatuses.READY_FOR_PICK, InvoiceStatuses.CANCELLED],
    [InvoiceStatuses.READY_FOR_PICK]: [
        InvoiceStatuses.DRAFT,
        InvoiceStatuses.PICK_COMPLETE,
        InvoiceStatuses.READY_FOR_TABC_STAMPS,
    ],
    [InvoiceStatuses.PICK_COMPLETE]: [
        InvoiceStatuses.READY_FOR_PICK,
        InvoiceStatuses.READY_FOR_TABC_STAMPS,
    ],
    [InvoiceStatuses.READY_FOR_TABC_STAMPS]: [
        InvoiceStatuses.READY_FOR_PICK,
        InvoiceStatuses.PICK_COMPLETE,
    ],
    [InvoiceStatuses.PARTIALLY_PAID]: [], // status changes only via payment add/remove
    [InvoiceStatuses.PAID]: [InvoiceStatuses.INVOICED],
    [InvoiceStatuses.INVOICED]: [InvoiceStatuses.DELIVERED, InvoiceStatuses.PICKED_UP],
    [InvoiceStatuses.DELIVERED]: [],
    [InvoiceStatuses.PICKED_UP]: [],
    [InvoiceStatuses.CANCELLED]: [],
};

const toStatusOptionDataCy = text =>
    `invoice_status_option_${text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/(^-|-$)/g, "")}`;

const isStatusDisabled = (currentInvoiceStatus, nextInvoiceStatus, invoiceDeliveryType) => {
    let transitionAllowed =
        ALLOWED_NEXT_STATUSES[currentInvoiceStatus]?.includes(nextInvoiceStatus);
    if (!transitionAllowed) return true;

    if (currentInvoiceStatus === InvoiceStatuses.INVOICED) {
        if (nextInvoiceStatus === InvoiceStatuses.DELIVERED) {
            return invoiceDeliveryType !== DeliveryTypes.DELIVERY;
        }
        if (nextInvoiceStatus === InvoiceStatuses.PICKED_UP) {
            return invoiceDeliveryType !== DeliveryTypes.PICKUP;
        }
    }

    return false;
};

const InvoiceComponentStatus = ({
    status,
    onChange,
    isFormik = false,
    name,
    label,
    noIcon,
    deliveryType,
    ...otherProps
}) => {
    if (!status) return null;
    const data = statusMapping[status];

    if (!data) return null;

    if (!onChange && !isFormik) {
        return (
            <Button
                data-cy="invoice_status"
                compact
                size="mini"
                color={data.color}
                content={data.text}
                icon={!noIcon && data.icon}
            />
        );
    }

    return isFormik ? (
        <FormikDropdown
            name={name}
            label={label}
            inputProps={{
                icon: !noIcon && data.icon,
                className: `icon  ${data.color} `,
                button: true,
                labeled: true,
                selection: false,
                "data-cy": "default_invoice",
            }}
            options={VISIBLE_INVOICE_STATUSES.map(value => ({
                key: value,
                value: value,
                text: statusMapping[value].text,
                icon: statusMapping[value].icon,
                "data-cy": toStatusOptionDataCy(statusMapping[value].text),
            }))}
            {...otherProps}
        />
    ) : (
        <Dropdown
            data-cy="invoice_status"
            button
            labeled
            floating
            value={status}
            text={data.text}
            onChange={onChange}
            selectOnBlur={false}
            icon={!noIcon && data.icon}
            className={`icon mini compact ${data.color}`}
            options={VISIBLE_INVOICE_STATUSES.map(is => ({
                key: is,
                value: is,
                text: statusMapping[is].text,
                icon: statusMapping[is].icon,
                "data-cy": toStatusOptionDataCy(statusMapping[is].text),
                disabled: isStatusDisabled(status, is, deliveryType),
            }))}
            {...otherProps}
        />
    );
};

InvoiceComponentStatus.propTypes = {
    noIcon: PropTypes.bool,
    status: PropTypes.string,
    onChange: PropTypes.func,
    deliveryType: PropTypes.string,
};

export default InvoiceComponentStatus;
